import { ReactNode, memo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks";
import { CircularProgress, Box } from "@mui/material";

/**
 * Enhanced protected route component with performance optimizations
 *
 * Features:
 * - Memoized to prevent unnecessary re-renders
 * - Role-based access control
 * - Clean loading state
 * - Preserves navigation state
 */

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  requiresOrg?: boolean;
}

const ProtectedRoute = ({
  children,
  requiredRoles = [],
  requiresOrg = false,
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, isInitialized, user } = useAuth();
  const location = useLocation();

  // Show loading indicator while auth is initializing
  if (isLoading || !isInitialized) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Redirect to login page, but save the location they were trying to access
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Check if user needs to reset password
  if (
    user?.forcePasswordChange &&
    location.pathname !== "/auth/forced-password-reset"
  ) {
    // User must reset password, redirect to forced password reset page
    return <Navigate to="/auth/forced-password-reset" replace />;
  }

  // Check if route requires org context
  if (requiresOrg && user?.isSuperAdmin && !user?.hasOrganization) {
    return <Navigate to="/admin" replace />;
  }

  // Check for required roles if specified
  if (requiredRoles.length > 0 && user) {
    // Special handling for SUPER_ADMIN requirement
    if (requiredRoles.includes("SUPER_ADMIN")) {
      if (!user.isSuperAdmin) {
        // User doesn't have super admin privileges
        return <Navigate to="/unauthorized" replace />;
      }
    } else {
      // Regular role checking
      const hasRequiredRole = requiredRoles.includes(user.role);

      if (!hasRequiredRole) {
        // User doesn't have required role, redirect to unauthorized page
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  // User is authenticated and has required role, render children
  return <>{children}</>;
};

// Memoize the component to prevent unnecessary re-renders
// This is particularly important for route components that could re-render often
export default memo(ProtectedRoute);
